"use client"

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuthToken } from "@/hooks/use-auth"
import {
  TemplateStatus,
  type Template,
  type TemplateDetail,
  type TemplateVersion,
  type TemplateType,
  type TemplateCategory,
  type StatusAction,
  type PreviewOutput,
} from "@audra-flow/types"

// API client configuration - empty string means same origin (Vite proxies /api to Express)
const API_BASE = ""

// ============================================
// Type Definitions (Re-export from types)
// ============================================

export type {
  Template,
  TemplateDetail,
  TemplateVersion,
  TemplateStatus,
  TemplateType,
  TemplateCategory,
}

export interface CreateTemplateInput {
  name: string
  description?: string
  icon?: string
  type: TemplateType
  category: TemplateCategory
  instructions?: string
  conversationStarters?: string[]
  outputFormatSpec?: string
  aiRole?: string
  schema?: Record<string, unknown>
  isGlobal?: boolean
}

export interface UpdateTemplateInput {
  name?: string
  description?: string | null
  icon?: string | null
  type?: TemplateType
  category?: TemplateCategory
  instructions?: string | null
  conversationStarters?: string[] | null
  outputFormatSpec?: string | null
  aiRole?: string | null
  schema?: Record<string, unknown>
}

export interface CreateVersionInput {
  version: string
  content: Record<string, unknown>
  schema?: Record<string, unknown>
  instructions?: string
  defaultValues?: Record<string, unknown>
  changelog?: string
}

export interface TemplateQueryParams {
  page?: number
  pageSize?: number
  search?: string
  type?: string
  category?: string
  status?: string
  ownerId?: string
  isDefault?: boolean
  showArchived?: boolean
  sortBy?: "name" | "updatedAt" | "createdAt" | "status"
  sortOrder?: "asc" | "desc"
}

// ============================================
// API Response Types
// ============================================

interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  meta: {
    pagination: {
      total: number
      page: number
      pageSize: number
      totalPages: number
    }
  }
}

interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

// ============================================
// Fetch helpers
// ============================================

async function fetchTemplates(
  token: string | null,
  params?: TemplateQueryParams
): Promise<{ templates: Template[]; total: number }> {
  if (!token) return { templates: [], total: 0 }

  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set("page", params.page.toString())
  if (params?.pageSize) searchParams.set("pageSize", params.pageSize.toString())
  if (params?.search) searchParams.set("search", params.search)
  if (params?.type) searchParams.set("type", params.type)
  if (params?.category) searchParams.set("category", params.category)
  if (params?.status) searchParams.set("status", params.status)
  if (params?.ownerId) searchParams.set("ownerId", params.ownerId)
  if (params?.isDefault !== undefined) searchParams.set("isDefault", params.isDefault.toString())
  if (params?.showArchived) searchParams.set("showArchived", "true")
  if (params?.sortBy) searchParams.set("sortBy", params.sortBy)
  if (params?.sortOrder) searchParams.set("sortOrder", params.sortOrder)

  const response = await fetch(
    `${API_BASE}/api/templates?${searchParams.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  )

  if (!response.ok) {
    if (response.status === 401) return { templates: [], total: 0 }
    throw new Error("Failed to fetch templates")
  }

  const data: PaginatedResponse<Template> = await response.json()
  return {
    templates: data.data,
    total: data.meta.pagination.total,
  }
}

async function fetchTemplate(
  templateId: string,
  token: string | null
): Promise<TemplateDetail | null> {
  if (!token) return null

  const response = await fetch(`${API_BASE}/api/templates/${templateId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    if (response.status === 404) return null
    throw new Error("Failed to fetch template")
  }

  const data: ApiResponse<TemplateDetail> = await response.json()
  return data.data
}

async function createTemplate(
  input: CreateTemplateInput,
  token: string | null
): Promise<Template> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/templates`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to create template")
  }

  const data: ApiResponse<Template> = await response.json()
  return data.data
}

async function updateTemplate(
  templateId: string,
  input: UpdateTemplateInput,
  token: string | null
): Promise<Template> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/templates/${templateId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to update template")
  }

  const data: ApiResponse<Template> = await response.json()
  return data.data
}

async function deleteTemplate(templateId: string, token: string | null): Promise<void> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/templates/${templateId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to delete template")
  }
}

async function createTemplateVersion(
  templateId: string,
  input: CreateVersionInput,
  token: string | null
): Promise<TemplateVersion> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/templates/${templateId}/versions`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to create version")
  }

  const data: ApiResponse<TemplateVersion> = await response.json()
  return data.data
}

// Map action to target status
const actionToStatus: Record<StatusAction, TemplateStatus> = {
  submit_for_review: TemplateStatus.IN_REVIEW,
  publish: TemplateStatus.ACTIVE,
  deprecate: TemplateStatus.DEPRECATED,
  revert_to_draft: TemplateStatus.DRAFT,
}

async function changeTemplateStatus(
  templateId: string,
  action: StatusAction,
  comment: string | undefined,
  token: string | null
): Promise<{ status: TemplateStatus }> {
  if (!token) throw new Error("Authentication required")

  const targetStatus = actionToStatus[action]
  if (!targetStatus) {
    throw new Error(`Unknown action: ${action}`)
  }

  const response = await fetch(`${API_BASE}/api/templates/${templateId}/status`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status: targetStatus, comment }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to change status")
  }

  const data = await response.json()
  return data.data
}

async function setDefaultTemplate(
  templateId: string,
  token: string | null
): Promise<{ isDefault: boolean }> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/templates/${templateId}/set-default`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ isDefault: true }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to set default")
  }

  const data = await response.json()
  return data.data
}

async function removeDefaultTemplate(
  templateId: string,
  token: string | null
): Promise<{ isDefault: boolean }> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/templates/${templateId}/set-default`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ isDefault: false }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to remove default")
  }

  const data = await response.json()
  return data.data
}

async function previewTemplate(
  templateId: string,
  input: { modelVersion?: string; testInput?: string },
  token: string | null
): Promise<PreviewOutput> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(`${API_BASE}/api/templates/${templateId}/preview`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.error?.message || error.message || "Failed to generate preview")
  }

  const data = await response.json()
  return data.data
}

// ============================================
// Query Hooks
// ============================================

export function useTemplates(params?: TemplateQueryParams) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["templates", params],
    queryFn: () => fetchTemplates(token, params),
    enabled: !!token,
  })
}

export function useTemplate(templateId: string) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["templates", templateId],
    queryFn: () => fetchTemplate(templateId, token),
    enabled: !!templateId && !!token,
  })
}

// ============================================
// Mutation Hooks
// ============================================

export function useCreateTemplate() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (input: CreateTemplateInput) => createTemplate(input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] })
    },
  })
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: ({ templateId, ...input }: UpdateTemplateInput & { templateId: string }) =>
      updateTemplate(templateId, input, token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["templates"] })
      queryClient.invalidateQueries({ queryKey: ["templates", variables.templateId] })
    },
  })
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (templateId: string) => deleteTemplate(templateId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] })
    },
  })
}

// Restore archived template
async function restoreTemplate(
  templateId: string,
  token: string | null
): Promise<Template> {
  if (!token) throw new Error("Not authenticated")

  const response = await fetch(`${API_BASE}/api/templates/${templateId}/restore`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error?.message || "Failed to restore template")
  }

  const data = await response.json()
  return data.data
}

export function useRestoreTemplate() {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (templateId: string) => restoreTemplate(templateId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] })
    },
  })
}

export function useCreateTemplateVersion(templateId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (input: CreateVersionInput) => createTemplateVersion(templateId, input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] })
      queryClient.invalidateQueries({ queryKey: ["templates", templateId] })
    },
  })
}

// ============================================
// Status Transition Hooks
// ============================================

export function useSubmitForReview(templateId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (comment?: string) => 
      changeTemplateStatus(templateId, "submit_for_review", comment, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] })
      queryClient.invalidateQueries({ queryKey: ["templates", templateId] })
    },
  })
}

export function usePublishTemplate(templateId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (comment?: string) => 
      changeTemplateStatus(templateId, "publish", comment, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] })
      queryClient.invalidateQueries({ queryKey: ["templates", templateId] })
    },
  })
}

export function useDeprecateTemplate(templateId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (comment?: string) => 
      changeTemplateStatus(templateId, "deprecate", comment, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] })
      queryClient.invalidateQueries({ queryKey: ["templates", templateId] })
    },
  })
}

export function useRevertToDraft(templateId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (comment?: string) => 
      changeTemplateStatus(templateId, "revert_to_draft", comment, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] })
      queryClient.invalidateQueries({ queryKey: ["templates", templateId] })
    },
  })
}

// ============================================
// Default Template Hooks
// ============================================

export function useSetDefaultTemplate(templateId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: () => setDefaultTemplate(templateId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] })
    },
  })
}

export function useRemoveDefaultTemplate(templateId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: () => removeDefaultTemplate(templateId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] })
    },
  })
}

// ============================================
// Preview Hook
// ============================================

export function usePreviewTemplate(templateId: string) {
  const token = useAuthToken()

  return useMutation({
    mutationFn: (input: { modelVersion?: string; testInput?: string }) => 
      previewTemplate(templateId, input, token),
  })
}

// ============================================
// Version Hooks
// ============================================

interface VersionSummary {
  id: string
  templateId: string
  version: string
  status: string
  content: Record<string, unknown>
  schema: Record<string, unknown> | null
  instructions: string | null
  changelog: string | null
  createdAt: string
  createdBy?: {
    id: string
    name: string | null
    email: string
  }
}

async function fetchTemplateVersions(
  templateId: string,
  token: string | null
): Promise<VersionSummary[]> {
  if (!templateId) return []
  
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}/api/templates/${templateId}/versions`, {
    headers,
  })

  if (!response.ok) {
    throw new Error("Failed to fetch versions")
  }

  const json = await response.json()
  return json.data || []
}

export function useTemplateVersions(templateId: string) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["templates", templateId, "versions"],
    queryFn: () => fetchTemplateVersions(templateId, token),
    enabled: !!templateId,
  })
}

export function useCreateVersion(templateId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (input: CreateVersionInput) => createTemplateVersion(templateId, input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates", templateId] })
      queryClient.invalidateQueries({ queryKey: ["templates", templateId, "versions"] })
    },
  })
}

// Activate a specific version (set as ACTIVE, others become DEPRECATED)
async function activateTemplateVersion(
  templateId: string,
  versionId: string,
  token: string | null
): Promise<{ id: string; version: string; status: string }> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(
    `${API_BASE}/api/templates/${templateId}/versions/${versionId}/activate`,
    {
      method: "PATCH",
      headers,
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error?.message || "Failed to activate version")
  }

  const json = await response.json()
  return json.data
}

export function useActivateVersion(templateId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()

  return useMutation({
    mutationFn: (versionId: string) => activateTemplateVersion(templateId, versionId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] })
      queryClient.invalidateQueries({ queryKey: ["templates", templateId] })
      queryClient.invalidateQueries({ queryKey: ["templates", templateId, "versions"] })
    },
  })
}